import moment from 'moment'
import { SEVEN_DAYS_AFTER_PURCHASE } from 'constants/cancellationPolicies'
import { INTL_DAY_MONTH_NAME_SHORT, INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { getChangeOfMindBeforeCheckInDays, getFlexibleCancellationDays } from 'lib/offer/cancellationPolicyUtils'

function hasPropertyTimezoneOffset(propertyTimezoneOffset: number | undefined) {
  return propertyTimezoneOffset !== undefined
}

interface FlexibleCancellationTimelineDetailsParams {
  cancellationPolicy: App.OfferCancellationPolicy
  checkInDate?: string | moment.Moment
  propertyTimezoneOffset?: number
  purchaseDate?: string
  today?: moment.Moment
}

export interface FlexibleCancellationTimelineDetails {
  today: moment.Moment
  checkInDate?: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment | undefined
  cancellationAfterChangeOfMindPeriod: boolean
  days: number
  changeOfMindDays: number
}

interface FlexibleCancellationTimelinePolicyDatesParams {
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment
}

export function getFlexibleCancellationTimelinePolicyDates(
  params: FlexibleCancellationTimelinePolicyDatesParams,
) {
  const {
    startBasedOnToday,
    startBasedOnCheckIn,
  } = params

  return {
    fullRefundDate: startBasedOnToday,
    partialRefundDate: moment(startBasedOnToday).add(1, 'day'),
    partialRefundDeadline: startBasedOnCheckIn,
    noRefundDate: moment(startBasedOnCheckIn).add(1, 'day'),
  }
}

export type FlexibleCancellationTimelinePolicyDates = ReturnType<typeof getFlexibleCancellationTimelinePolicyDates>

export type FlexibleCancellationTimelinePeriod = 'full_refund' | 'credit_or_partial' | 'no_refund'

/**
 * Formats a cancellation-policy timeline date the same way as the with-dates modal/timeline.
 */
export function formatFlexibleCancellationPolicyDate(
  date: string | moment.Moment,
  today: moment.Moment,
  formatDate: (date: moment.Moment, options: Intl.DateTimeFormatOptions) => string,
) {
  const parsedDate = moment(date)
  const options = parsedDate.year() !== today.year() ?
    INTL_DMY_CASUAL_SHORT_FORMAT :
    INTL_DAY_MONTH_NAME_SHORT
  return formatDate(parsedDate, options)
}

/**
 * Resolves the customer's current refund period from timeline dates + today.
 * Mirrors which nodes FlexibleCancellationPolicyModalWithDates would emphasise.
 */
export function getFlexibleCancellationTimelineCurrentPeriod(params: {
  today: moment.Moment
  policyDates: FlexibleCancellationTimelinePolicyDates
  cancellationAfterChangeOfMindPeriod: boolean
}): FlexibleCancellationTimelinePeriod {
  const { today, policyDates, cancellationAfterChangeOfMindPeriod } = params

  if (today.isSameOrBefore(policyDates.fullRefundDate, 'day')) {
    return 'full_refund'
  }

  if (
    cancellationAfterChangeOfMindPeriod &&
    today.isSameOrBefore(policyDates.partialRefundDeadline, 'day')
  ) {
    return 'credit_or_partial'
  }

  return 'no_refund'
}

export function getFlexibleCancellationTimelineNextPeriod(
  currentPeriod: FlexibleCancellationTimelinePeriod,
  cancellationAfterChangeOfMindPeriod: boolean,
): FlexibleCancellationTimelinePeriod | 'check_in' {
  if (currentPeriod === 'full_refund') {
    return cancellationAfterChangeOfMindPeriod ? 'credit_or_partial' : 'no_refund'
  }

  if (currentPeriod === 'credit_or_partial') {
    return 'no_refund'
  }

  return 'check_in'
}

function getTimelineMoment(
  date: string | moment.Moment | undefined,
  propertyTimezoneOffset: number | undefined,
) {
  if (!date) {
    return undefined
  }

  const parsedDate = moment(date)
  return hasPropertyTimezoneOffset(propertyTimezoneOffset) ? parsedDate.utcOffset(propertyTimezoneOffset, true) : parsedDate
}

export function getFlexibleCancellationTimelineDetails(
  params: FlexibleCancellationTimelineDetailsParams,
): FlexibleCancellationTimelineDetails {
  const {
    cancellationPolicy,
    checkInDate,
    propertyTimezoneOffset,
    purchaseDate,
    today,
  } = params

  const purchaseMoment = purchaseDate ? moment(purchaseDate) : moment()
  const timelineToday = today ?? (hasPropertyTimezoneOffset(propertyTimezoneOffset) ? moment().utcOffset(propertyTimezoneOffset) : moment())
  const days = getFlexibleCancellationDays(cancellationPolicy.type)
  const changeOfMindDays = getChangeOfMindBeforeCheckInDays(cancellationPolicy.type)
  const checkInMoment = getTimelineMoment(checkInDate, propertyTimezoneOffset)

  let startBasedOnToday = hasPropertyTimezoneOffset(propertyTimezoneOffset) ?
      purchaseMoment.clone().utcOffset(propertyTimezoneOffset).add(SEVEN_DAYS_AFTER_PURCHASE, 'days') :
      purchaseMoment.clone().add(SEVEN_DAYS_AFTER_PURCHASE, 'days')
  let startBasedOnCheckIn = checkInMoment?.clone().subtract(days, 'days')

  const changeOfMindDeadline = checkInMoment?.clone().subtract(changeOfMindDays, 'days')
  if (changeOfMindDeadline) {
    startBasedOnToday = startBasedOnToday.isBefore(changeOfMindDeadline) ? startBasedOnToday : changeOfMindDeadline
  }

  const cancellationAfterChangeOfMindPeriod = !!(
    checkInMoment &&
    changeOfMindDeadline &&
    startBasedOnToday.isBefore(changeOfMindDeadline) &&
    purchaseMoment.clone().add(SEVEN_DAYS_AFTER_PURCHASE, 'days').isBefore(checkInMoment.clone().subtract(days, 'days'))
  )
  startBasedOnCheckIn = cancellationAfterChangeOfMindPeriod ? startBasedOnCheckIn : startBasedOnToday

  return {
    today: timelineToday,
    checkInDate,
    startBasedOnToday,
    startBasedOnCheckIn,
    cancellationAfterChangeOfMindPeriod,
    days,
    changeOfMindDays,
  }
}
